<template>
  <b-modal
    v-model="editorShown"
    title="System Logs"
    header-bg-variant="primary"
    header-text-variant="gray"
    header-class="bg-white align-items-center mb-2 border-bottom-0"
    no-close-on-backdrop
    hide-footer
    modal-class="modal-fullscreen"
    body-class="pt-0"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
        style="padding: 13px 12px 0px"
      >
        <div>
          <h5 class="modal-title font-weight-bold">System Logs</h5>
        </div>
        <view-switch :options="viewOptions" v-model="currentView" />
        <button
          type="button"
          aria-label="Close"
          class="close"
          style="opacity:1 !important; margin-left:initial !important"
          @click="closeModal"
        >
          ×
        </button>
      </div>
    </template>
    <div class="mx-2">
      <div
        class="d-flex flex-row justify-content-between align-items-center mb-3"
      >
        <div
          class="d-flex flex-row justify-content-start align-items-center"
          style="width:220px;"
        ></div>

        <div
          class="d-flex align-items-center justify-content-end"
          style="width:220px;"
        ></div>
      </div>
      <audit-modal-filters
        v-model="filter"
        :hasFilters="hasFilters"
        :loading="false"
        ref="filterHeight"
      />
      <div :style="`--filter-height:` + filterHeight()">
        <audit-history-list-view
          :exportFilter="historyExportFilter"
          :list="historyList"
          :listTotal="historyListTotal"
          :orderBy="filter.orderBy"
          :orderByDescending="filter.orderByDescending"
          ref="historyList"
          v-show="currentView === 'history'"
          :showing="currentView === 'history'"
          :page="historyPage"
          :page-size="historyPageSize"
          @change-page="changeHistoryPage"
          @change-order="changeOrder"
        >
        </audit-history-list-view>
        <audit-auth-list-view
          :exportFilter="authExportFilter"
          :list="authList"
          :listTotal="authListTotal"
          :orderBy="filter.orderBy"
          :orderByDescending="filter.orderByDescending"
          ref="authList"
          v-show="currentView === 'auth'"
          :showing="currentView === 'auth'"
          :page="authPage"
          :page-size="authPageSize"
          @change-page="changeAuthPage"
          @change-order="changeOrder"
        >
        </audit-auth-list-view>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ViewSwitch from "../controls/ViewSwitch";
import AuditModalFilters from "./AuditModalFilters";
import AuditHistoryListView from "./AuditHistoryListView";
import AuditAuthListView from "./AuditAuthListView";
import audit from "../../server/audit";
import { formatEndDateSql, formatStartDateSql } from "../../helpers/date";

export default {
  name: "AuditModal",
  components: {
    ViewSwitch,
    AuditModalFilters,
    AuditHistoryListView,
    AuditAuthListView
  },
  data() {
    return {
      currentView: "history",
      filter: {
        searchPhrase: "",
        orderBy: "datetime",
        orderByDescending: true,
        users: [],
        locations: [],
        datetimeFrom: null,
        datetimeTo: null
      },
      authPage: 0,
      authPageSize: 50,
      historyPage: 0,
      historyPageSize: 50,
      authList: [],
      authListTotal: 0,
      historyList: [],
      historyListTotal: 0
    };
  },
  async mounted() {
    await this.fetchAll();
    this.filterHeight();
  },
  methods: {
    fetchAll(instantReload = false) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(
        async () => {
          const filterObj = {
            locations: this.filter.locations.map(x => x.value),
            users: this.filter.users.map(x => x.value),
            datetimeFrom: this.filter.datetimeFrom
              ? formatStartDateSql(this.filter.datetimeFrom)
              : null,
            datetimeTo: this.filter.datetimeTo
              ? formatEndDateSql(this.filter.datetimeTo)
              : null,
            searchPhrase: this.filter.searchPhrase,
            orderBy: this.filter.orderBy,
            orderByDescending: this.filter.orderByDescending,
            page: this.authPage + 1,
            pageSize: this.authPageSize
          };
          const authResult = await audit.queryAuth(filterObj);
          this.authList = authResult.records;
          this.authListTotal = authResult.count;
          filterObj.page = this.historyPage + 1;
          filterObj.pageSize = this.historyPageSize;
          const historyResult = await audit.queryHistory(filterObj);
          this.historyList = historyResult.records;
          this.historyListTotal = historyResult.count;
        },
        instantReload ? 0 : 1000
      );
    },
    filterHeight() {
      return (
        (this.$refs.filterHeight
          ? this.$refs.filterHeight.$el.clientHeight
          : 0) + "px"
      );
    },
    resetList() {
      this.authList = [];
      this.authListTotal = 0;
      this.historyList = [];
      this.historyListTotal = 0;
      this.$refs.historyList.changePage(0);
      this.$refs.authList.changePage(0);
    },
    changePage(pagination) {
      this.authPage = pagination.page;
      this.historyPage = pagination.page;
      this.fetchAll();
    },
    changeHistoryPage(pagination) {
      this.historyPage = pagination.page;
      this.fetchAll();
    },
    changeAuthPage(pagination) {
      this.authPage = pagination.page;
      this.fetchAll();
    },
    changeOrder(newOrder) {
      this.filter.orderBy = newOrder.orderBy;
      this.filter.orderByDescending = newOrder.orderByDescending;
      this.resetList();
      this.$refs.historyList.changePage(0);
      this.$refs.authList.changePage(0);
    },
    closeModal() {
      this.$emit("input", false);
    }
  },
  computed: {
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    viewOptions() {
      return [
        {
          value: "history",
          label: "History",
          icon: "fas fa-th-large"
        },
        {
          value: "auth",
          label: "Auth",
          icon: "fas fa-list-ul"
        }
      ];
    },
    hasFilters() {
      return (
        this.filter.searchPhrase != "" ||
        this.filter.users.length > 0 ||
        this.filter.locations.length > 0
      );
    },
    authExportFilter() {
      return {
        ...this.exportFilter,
        typeStatusValues: ["auth"]
      };
    },
    historyExportFilter() {
      return {
        ...this.exportFilter,
        typeStatusValues: ["history"]
      };
    },
    exportFilter() {
      return {
        searchPhrase: this.filter.searchPhrase,
        providerNames: this.filter.users.map(l => l.value),
        locationNames: this.filter.locations.map(l => l.value),
        dateFrom: this.filter.datetimeFrom,
        dateTo: this.filter.datetimeTo,
        orderByStatusValues: [this.filter.orderBy],
        sortStatusValues: this.filter.orderByDescending ? ["desc"] : ["asc"]
      };
    }
  },
  watch: {
    "filter.datetime": function() {
      this.resetList();
      this.changePage({
        page: 0
      });
    },
    "filter.users": function() {
      this.resetList();
      this.changePage({
        page: 0
      });
    },
    "filter.locations": function() {
      this.resetList();
      this.changePage({
        page: 0
      });
    }
  }
};
</script>

<style>
.modal-fullscreen .modal-dialog {
  max-width: 100%;
  margin: 20px;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 96vh;
  display: flex;
  position: fixed;
  z-index: 100000;
}
</style>
